import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useAuth } from '../../AuthContext';
import { tokens } from '../../theme';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import sleeping from '../../assets/icons/sleeping.png';
import LaptopIcon from '@mui/icons-material/Laptop';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';

const Home = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData } = useAuth();
  const [leave, setLeave] = useState([]);
  const [borrow, setBorrow] = useState([]);
  const [laptops, setLaptops] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusText = {
    0: 'Pending',
    1: 'Inprogress',
    2: 'Approved',
    3: 'Rejected',
    4: 'Returned',
  };

  const statusColor = {
    0: colors.orange[100],
    1: colors.green[100],
    2: colors.blue[100],
    3: colors.red[100],
    4: colors.blue[100],
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [laptopResponse, borrowResponse, leaveResponse] = await Promise.all([
          getData('proccess.php', 'inventory'),
          getData('proccess.php', 'borrow_V2', userId),
          getData('proccess.php', 'leaves_V2', userId),
        ]);

        if (laptopResponse.success) {
          setLaptops(laptopResponse.data);
        }

        if (borrowResponse.success) {
          setBorrow(borrowResponse.data);
        }

        if (leaveResponse.success) {
          setLeave(leaveResponse.data);
        }
      } catch (err) {
        toast.error('Network error: Failed to fetch data');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [userId, getData]);

  // Transform leave data into calendar events
  const calendarEvents = leave.map(leave => ({
    title: `${leave.leave_type} Leave (${statusText[leave.status]})`,
    start: leave.startDate,
    end: leave.endDate,
    backgroundColor: statusColor[leave.status],
    borderColor: statusColor[leave.status],
  }));

  // Compute data for charts
  const getChartData = () => {
    // Line Chart: Most Borrowed Item
    const borrowCounts = borrow.reduce((acc, b) => {
      acc[b.equipment] = (acc[b.equipment] || 0) + 1;
      return acc;
    }, {});
    const mostBorrowedId = Object.keys(borrowCounts).reduce((a, b) => 
      borrowCounts[a] > borrowCounts[b] ? a : b, null
    ) || '0';
    const mostBorrowedLaptop = laptops.find(l => l.id === mostBorrowedId);
    const mostBorrowedName = mostBorrowedLaptop ? mostBorrowedLaptop.name : 'Unknown';

    // Group borrows by month for the most borrowed item
    const months = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      return date.toISOString().slice(0, 7); // YYYY-MM
    }).reverse();

    const borrowByMonth = months.map(month => {
      return borrow.filter(b => b.equipment === mostBorrowedId && b.date.startsWith(month)).length;
    });

    // Format for Nivo Line Chart
    const lineChartData = [
      {
        id: `Borrows of ${mostBorrowedName}`,
        data: months.map((month, index) => ({
          x: month,
          y: borrowByMonth[index],
        })),
      },
    ];

    // Pie Chart: Borrowed vs Available
    const availableCount = laptops.filter(l => l.status === 0).length;
    const borrowedCount = laptops.filter(l => l.status !== 0).length;

    // Format for Nivo Pie Chart
    const pieChartData = [
      { id: 'Borrowed', label: 'Borrowed', value: borrowedCount, color: colors.red[400] },
      { id: 'Available', label: 'Available', value: availableCount, color: colors.green[400] },
    ];

    return { lineChartData, pieChartData };
  };

  const chartData = getChartData();

  return (
    <Box mt={2}>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Box
            width="100%"
            display="flex"
            flexWrap="wrap"
            gap="1rem"
            padding="1rem"
            marginBottom="2rem"
          >
            <Card sx={{ backgroundColor: colors.primary[500], flex: '1', minWidth: '100px' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">Pending Borrowing</Typography>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ backgroundColor: colors.primary[500], flex: '1', minWidth: '100px' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">Pending Leaves</Typography>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ backgroundColor: colors.primary[500], flex: '1', minWidth: '100px' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">Rejected Borrowing</Typography>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ backgroundColor: colors.primary[500], flex: '1', minWidth: '100px' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">Rejected Leaves</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box display="flex" flexWrap="wrap-reverse" gap={'.5rem'}>
            <Box flex="1" minWidth="350px">
              <Box display="flex" flexWrap="wrap" gap={'.5rem'}>
                <Box flex="1" minWidth="350px">
                  <Typography variant="h5" fontWeight="700">
                    My Borrowing
                  </Typography>
                  <Box>
                    {borrow.length > 0 ? (
                      <Box marginTop="1rem">
                        {borrow.map(borrow => {
                          const laptop = laptops.find(s => s.id === borrow.equipment);
                          return (
                            <Card
                              key={borrow.id}
                              sx={{
                                backgroundColor: statusColor[borrow.status],
                                marginBottom: '1rem',
                                color: 'black',
                              }}
                            >
                              <CardContent>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  marginBottom="1rem"
                                >
                                  {laptop ? (
                                    <LaptopIcon />
                                  ) : (
                                    <Typography>Laptop not found</Typography>
                                  )}
                                  <Typography variant="h5" fontWeight="bold">
                                    {laptop ? `${laptop.Type}: ${laptop.name}` : 'Unknown Laptop'}
                                  </Typography>
                                  <Typography>
                                    Condition: {laptop ? laptop.product_condition : 'N/A'}
                                  </Typography>
                                </Box>
                                <Box
                                  display="flex"
                                  flexWrap="wrap"
                                  justifyContent="space-between"
                                  p={1}
                                  borderRadius=".2rem 0 0 .2rem"
                                  sx={{
                                    background:
                                      'linear-gradient(90deg, rgba(31, 179, 31, 1), transparent)',
                                  }}
                                >
                                  <Typography>FROM: {borrow.date}</Typography>
                                  <Typography>FOR: {borrow.duration}</Typography>
                                  <Typography>Borrow Type: {borrow.type}</Typography>
                                  <Typography>Destination: {borrow.destination}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="h5" fontWeight="bold">
                                    Status: {statusText[borrow.status]}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box>
                        <Typography>No Data Found</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box flex="1" minWidth="350px">
                  <Typography variant="h5" fontWeight="700">
                    My Leave
                  </Typography>
                  <Box>
                    {leave.length > 0 ? (
                      <Box marginTop="1rem">
                        <Card
                          sx={{
                            backgroundColor: statusColor[leave[0].status],
                            color: 'black',
                          }}
                        >
                          <CardContent>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <img src={sleeping} style={{ width: '2rem' }} alt="Sleeping icon" />
                              <Typography variant="h5" fontWeight={700}>
                                {leave[0].leave_type} leave
                              </Typography>
                            </Box>
                            <Box
                              marginTop="1rem"
                              display="flex"
                              justifyContent="space-between"
                            >
                              <Typography>
                                FROM: {leave[0].startDate} - {leave[0].endDate}
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                STATUS: {statusText[leave[0].status]}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    ) : (
                      <Box>
                        <Typography>No Data Found</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
                <Typography variant="h5" fontWeight="700" marginTop={4}>
                    Available Laptops
                </Typography>
                <Box paddingTop="1rem">
                    {laptops.length > 0 ? (
                    <Box>
                        {laptops.map(laptop =>
                        laptop.status === 0 && laptop.categoryType === 'Laptop' && laptop.product_condition !== 'Damaged' && laptop.product_condition !== 'Refurbished' ? (
                            <Card key={laptop.id}>
                            <CardContent>
                                <Box
                                display="flex"
                                justifyContent="space-between"
                                marginBottom="0.5rem"
                                >
                                <Typography>
                                    {laptop.Type}: {laptop.name}
                                </Typography>
                                <Typography fontWeight={700}>ID: {laptop.id}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                <Typography>SKU: {laptop.sku}</Typography>
                                <Box display="flex">
                                    <Typography>No:</Typography>
                                    <Typography variant="h4" fontWeight={700}>
                                    {laptop.productNumber}
                                    </Typography>
                                </Box>
                                </Box>
                            </CardContent>
                            </Card>
                        ) : null,
                        )}
                        {laptops.every(laptop => laptop.status !== 0) && (
                        <Typography>No Available Laptops</Typography>
                        )}
                    </Box>
                    ) : (
                    <Box>
                        <Typography>No Data Found</Typography>
                    </Box>
                    )}
                </Box>
                {/* Charts Section */}
                <Box mt="2rem" display="flex" flexWrap="wrap" gap="1rem">
                    <Box flex="1" minWidth="350px" maxWidth="600px">
                    <Typography variant="h5" fontWeight="700" mb="1rem">
                        Most Borrowed Item Over Time
                    </Typography>
                    {chartData.lineChartData[0].data.some(point => point.y > 0) ? (
                        <Card sx={{ backgroundColor: colors.primary[500], padding: '1rem', height: '250px' }}>
                        <ResponsiveLine
                            data={chartData.lineChartData}
                            margin={{ top: 50, right: 50, bottom: 70, left: 60 }}
                            xScale={{ type: 'point' }}
                            yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false }}
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Month',
                            legendOffset: 36,
                            legendPosition: 'middle',
                            }}
                            axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: 'Number of Borrows',
                            legendOffset: -40,
                            legendPosition: 'middle',
                            }}
                            colors={[colors.blue[400]]}
                            pointSize={10}
                            pointColor={{ theme: 'background' }}
                            pointBorderWidth={2}
                            pointBorderColor={{ from: 'serieColor' }}
                            pointLabel="y"
                            pointLabelYOffset={-12}
                            useMesh={true}
                            theme={{
                            axis: {
                                legend: { text: { fill: colors.primary[100] } },
                                ticks: { text: { fill: colors.primary[100] } },
                            },
                            legends: { text: { fill: colors.primary[100] } },
                            }}
                            legends={[
                            {
                                anchor: 'top',
                                direction: 'row',
                                justify: false,
                                translateX: 0,
                                translateY: -40,
                                itemsSpacing: 0,
                                itemWidth: 150,
                                itemHeight: 20,
                                itemTextColor: colors.primary[100],
                                symbolSize: 12,
                                symbolShape: 'circle',
                            },
                            ]}
                        />
                        </Card>
                    ) : (
                        <Typography>No borrowing data available for the most borrowed item.</Typography>
                    )}
                    </Box>
                    <Box flex="1" minWidth="350px" maxWidth="400px">
                    <Typography variant="h5" fontWeight="700" mb="1rem">
                        Borrowed vs Available Items
                    </Typography>
                    {chartData.pieChartData.some(item => item.value > 0) ? (
                        <Card sx={{ backgroundColor: colors.primary[500], padding: '1rem', height: '250px' }}>
                        <ResponsivePie
                            data={chartData.pieChartData}
                            margin={{ top: 40, right: 50, bottom: 50, left: 50 }}
                            innerRadius={0.5}
                            padAngle={0.7}
                            cornerRadius={3}
                            colors={chartData.pieChartData.map(item => item.color)}
                            borderWidth={1}
                            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor={colors.primary[100]}
                            arcLinkLabelsThickness={2}
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLabelsSkipAngle={10}
                            arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                            theme={{
                            labels: { text: { fill: colors.primary[100] } },
                            legends: { text: { fill: colors.primary[100] } },
                            }}
                            legends={[
                            {
                                anchor: 'top',
                                direction: 'row',
                                justify: false,
                                translateX: 0,
                                translateY: -40,
                                itemsSpacing: 0,
                                itemWidth: 100,
                                itemHeight: 17,
                                itemTextColor: colors.primary[100],
                                symbolSize: 17,
                                symbolShape: 'circle',
                            },
                            ]}
                        />
                        </Card>
                    ) : (
                        <Typography>No laptop data available.</Typography>
                    )}
                    </Box>
                </Box>
            </Box>
            <Box flex={1} minWidth="350px" maxWidth="500px" overflow="auto">
              <Typography variant="h5" fontWeight="700">
                Event Calendar
              </Typography>
              <Box
                sx={{
                  backgroundColor: colors.primary[500],
                  padding: '1rem',
                  borderRadius: '4px',
                  marginTop: '1rem',
                }}
              >
                <FullCalendar
                  plugins={[dayGridPlugin, timeGridPlugin]}
                  initialView="dayGridMonth"
                  events={calendarEvents}
                  height="auto"
                  eventDisplay="block"
                  eventTextColor={colors.primary[400]}
                  headerToolbar={{
                    left: 'prev,next',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay',
                  }}
                />
              </Box>
            </Box>
          </Box>

          
        </>
      )}
    </Box>
  );
};

export default Home;